import React, { use, useEffect, useState } from "react";
import ManifestSelectModal from "./ManifestSelectModal";
import { getManifests } from "../services/manifestService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Trash2 } from "lucide-react";
import CustomDropdown from "./CustomDropdown";
import {
  routeOptions,
  vehicleModelOptions,
  vendorTypeOptions,
} from "../utils/helper";
import { getNextTripSheetNo } from "../services/tripSheetService";

const TripSheetForm = ({ tripSheet, setTripSheet, isPreview = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedManifests, setSelectedManifests] = useState([]);
  const [manifestData, setManifestData] = useState([]);
  const { user } = useSelector((state) => state.user);
  // console.log("User-->", user);
  /* -------- DO NOT CHANGE YOUR LOGIC -------- */
  const addSelectedManifests = () => {
    const existing = tripSheet.manifests.map((s) => s.manifestNo);

    const newRows = selectedManifests
      .filter((m) => !existing.includes(m))
      .map((m) => {
        const manifestSelectData = manifestData?.find(
          (item) => item.manifestNo === m,
        );

        return {
          manifestNo: m,
          startLocation: user?.branch?.name || "",
          endLocation: manifestSelectData?.destination || "",
          totalPkg: manifestSelectData?.totalConsignments || 0,
          totalWeight: manifestSelectData?.totalWeight || 0,
        };
      });

    setTripSheet((prev) => ({
      ...prev,
      manifests: [...prev.manifests, ...newRows],
    }));

    setSelectedManifests([]);
    setIsModalOpen(false);
  };
  const handleGetManifestData = async () => {
    try {
      const res = await getManifests(user?.branch?.code || "");
      if (res?.status === 200) {
        setManifestData(res?.data);
      } else {
        toast.error("Failed to get manifest data. Please try again.");
      }
    } catch (error) {
      console.log("Failed to get manifest data", error);
      toast.error("Failed to get manifest data. Please try again.");
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setTripSheet((prev) => ({ ...prev, [name]: value }));
  };

  const handleRowChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...tripSheet.manifests];
    updated[index][name] = value;

    setTripSheet((prev) => ({
      ...prev,
      manifests: updated,
    }));
  };

  const handleDeleteRow = (index) => {
    setTripSheet((prev) => ({
      ...prev,
      manifests: prev.manifests.filter((_, i) => i !== index),
    }));
  };
  const handleManifestModalClose = () => {
    setIsModalOpen(false);
    setSelectedManifests([]);
  };
  useEffect(() => {
    handleGetManifestData();
  }, [user?.branch?.code]);
  useEffect(() => {
    const start = Number(tripSheet?.startKm) || 0;
    const end = Number(tripSheet?.endKm) || 0;

    const total = end >= start ? end - start : "";

    setTripSheet((prev) => ({
      ...prev,
      totalKmDriven: total,
    }));
  }, [tripSheet?.startKm, tripSheet?.endKm]);

  useEffect(() => {
    const totalPackages = tripSheet?.manifests?.reduce(
      (sum, s) => sum + Number(s.totalPkg || 0),
      0,
    );
    const totalWeight = tripSheet?.manifests?.reduce(
      (sum, s) => sum + Number(s.totalWeight || 0),
      0,
    );

    setTripSheet((prev) => ({
      ...prev,
      totalPackages,
      totalWeight,
    }));
  }, [tripSheet?.manifests]);

  useEffect(() => {
    const totalExpenses =
      Number(tripSheet?.fuel1 || 0) +
        Number(tripSheet?.fuel2 || 0) +
        Number(tripSheet?.totalTollsPaid || 0) +
        Number(tripSheet?.unloadingCharges || 0) +
        Number(tripSheet?.otherExpenses || 0) || 0;

    const perKgExpenses =
      tripSheet?.totalWeight > 0
        ? (totalExpenses / tripSheet.totalWeight).toFixed(2)
        : 0;

    setTripSheet((prev) => ({
      ...prev,
      totalExpenses,
      perKgExpenses,
    }));
  }, [
    tripSheet?.fuel1,
    tripSheet?.fuel2,
    tripSheet?.totalTollsPaid,
    tripSheet?.unloadingCharges,
    tripSheet?.otherExpenses,
    tripSheet?.totalWeight,
  ]);

  return (
    <div className="bg-white p-3 text-xs rounded-xl shadow">
      {/* FORM FIELDS */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <CustomDropdown
            label="Vendor Type"
            // required
            value={tripSheet.vendorType}
            options={vendorTypeOptions}
            onChange={(val) => setTripSheet({ ...tripSheet, vendorType: val })}
          />
        </div>
        <div>
          <CustomDropdown
            label="Route"
            required
            value={tripSheet.routeName}
            options={routeOptions}
            onChange={(val) => setTripSheet({ ...tripSheet, routeName: val })}
          />
        </div>
        <div>
          <CustomDropdown
            label="Vehicle Model"
            // required
            value={tripSheet.vehicleModel}
            options={vehicleModelOptions}
            onChange={(val) =>
              setTripSheet({ ...tripSheet, vehicleModel: val })
            }
          />
        </div>
        <div>
          <div className="font-semibold mb-1">
            Trip Sheet No<span className="text-red-500">*</span>
          </div>

          <div className="flex gap-2">
            <input
              name="tripSheetNo"
              value={tripSheet.tripSheetNo || ""}
              readOnly
              placeholder="Click Generate"
              className="w-full text-sm border px-2 py-2 rounded bg-gray-100"
            />
            {!isPreview && (
              <button
                type="button"
                disabled={!!tripSheet.tripSheetNo}
                onClick={async () => {
                  try {
                    const res = await getNextTripSheetNo(); // 👈 API

                    setTripSheet((prev) => ({
                      ...prev,
                      tripSheetNo: res?.data?.tripSheetNo,
                    }));
                  } catch (err) {
                    console.error(err);
                    toast.error("Failed to generate Trip Sheet No");
                  }
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded text-xs"
              >
                Generate
              </button>
            )}
          </div>
        </div>
        {[
          // ["Vender Type", "vendorType"],
          // ["Trip Date", "tripDate"],
          // ["Route Name", "routeName"],
          // ["Vehicle Model", "vehicleModel"],
          // ["Trip Sheet No", "tripSheetNo", true],
          ["Trip Hire Contact", "tripHireContact"],
          ["Vehicle Number", "vehicleNumber"],
          ["Vendor Name", "vendorName"],
          ["Driver Name", "driverName"],
          ["Mobile Number", "mobileNumber"],
          ["License Number", "licenseNumber"],
        ]?.map(([label, name, required]) => (
          <div key={name}>
            <div className="font-semibold mb-1">
              {label}
              {required && <span className="text-red-500">*</span>}
            </div>

            <input
              name={name}
              required={required}
              value={tripSheet[name] || ""}
              onChange={handleChange}
              className="w-full text-sm border px-2 py-2 rounded focus:ring-1 focus:ring-sky-500"
            />
          </div>
        ))}
        {/* <div className="grid grid-cols-3 gap-4 mb-4"> */}
        {[
          ["Start KM", "startKm"],
          ["Ending KM", "endKm"],
        ]?.map(([label, name]) => (
          <div key={name}>
            <div className="font-semibold mb-1">{label}</div>
            <input
              name={name}
              value={tripSheet[name] || ""}
              onChange={handleChange}
              className="w-full text-sm border px-2 py-2 rounded focus:ring-1 focus:ring-sky-500"
            />
          </div>
        ))}
        {/* </div> */}
      </div>

      {/* Shipments */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-sm font-semibold">Shipment Details</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          + Add Manifest
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300 border-collapse rounded-lg ">
          <thead className="bg-slate-100 text-xs">
            <tr>
              {[
                "SL",
                "Manifest No",
                "Start Location",
                "End Location",
                "Total Pkg",
                "Total Weight",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 border text-left border-gray-300"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tripSheet?.manifests?.map((row, index) => (
              <tr key={index} className="text-sm">
                <td className="px-3 py-2 text-center border border-gray-300">
                  {index + 1}
                </td>
                <td className="px-3 py-2 border border-gray-300">
                  <input
                    readOnly
                    value={row.manifestNo}
                    className="w-full   py-1 rounded outline-none"
                  />
                </td>

                {[
                  "startLocation",
                  "endLocation",
                  "totalPkg",
                  "totalWeight",
                ].map((field) => (
                  <td key={field} className="px-3 py-2 border border-gray-300">
                    <input
                      name={field}
                      readOnly
                      value={row[field] || ""}
                      // onChange={(e) => handleRowChange(e, index)}
                      className="w-full  rounded  py-1 outline-none"
                    />
                  </td>
                ))}

                <td className="px-3 py-2 text-center border border-gray-300">
                  <button
                    onClick={() => handleDeleteRow(index)}
                    className="text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}

            {tripSheet?.manifests?.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-6 text-slate-400  border-gray-300"
                >
                  No Manifest added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="font-semibold mb-1">Total Packages</div>
          <div className="border rounded px-2 py-1 font-bold">
            {tripSheet?.manifests?.reduce(
              (sum, s) => sum + Number(s.totalPkg || 0),
              0,
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="font-semibold mb-1">Total Weight</div>
          <div className="border rounded px-2 py-1 font-bold">
            {tripSheet?.manifests?.reduce(
              (sum, s) => sum + Number(s.totalWeight || 0),
              0,
            )}{" "}
            kg
          </div>
        </div>
      </div>

      {/* Expense Section */}
      <div className="grid grid-cols-6 gap-3 pt-6 text-sm">
        {[
          ["Total KM Driven", "totalKmDriven"],
          ["Fluel 1st", "fuel1"],
          ["Fluel 2nd", "fuel2"],
          ["Total Tolls Paid", "totalTollsPaid"],
          ["Unloading Charges", "unloadingCharges"],
          ["Other Expenses", "otherExpenses"],
        ].map(([label, name]) => (
          <div key={name}>
            <div className="font-semibold pb-1 text-xs">{label}</div>
            <input
              name={name}
              readOnly={name === "totalKmDriven"}
              value={tripSheet[name] || ""}
              onChange={handleChange}
              className="border rounded px-2 py-2 w-full"
            />
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-6">
        <div className="gap-4 flex text-sm">
          {[
            ["Per Kg Expences", "perKgExpenses"],
            ["Total Expences", "totalExpenses"],
          ].map(([label, name]) => (
            <div key={name}>
              <div className="font-semibold pb-1 text-xs">{label}</div>
              <input
                name={name}
                readOnly
                value={tripSheet[name] || ""}
                onChange={handleChange}
                className="border rounded px-2 py-2 w-full"
              />
            </div>
          ))}
        </div>

        {/* <div className="flex gap-20">
          <div>Date</div>
          <div>OPS Master Signature</div>
          <div>Driver's Signature</div>
        </div> */}
      </div>

      <ManifestSelectModal
        isOpen={isModalOpen}
        onClose={handleManifestModalClose}
        availableManifests={manifestData?.map((m) => m.manifestNo)}
        selectedManifests={selectedManifests}
        setSelectedManifests={setSelectedManifests}
        onConfirm={addSelectedManifests}
      />
    </div>
  );
};

export default TripSheetForm;
